import { useMemo, useRef, useState } from "react";
import {
  Button,
  Icon,
  Toast,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { industrySectors, type IndustrySector } from "../../api/industrySectors.service";
import { DataTable } from "../../components/DataTable/DataTable";
import EmptyState from "../../components/EmptyState";
import { ErrorBanner } from "../../components/ErrorBanner/ErrorBanner";
import LoadingState from "../../components/LoadingState";
import { PageHeader } from "../../components/PageHeader";
import { TAXONOMY_DELETE_PERMISSION, TAXONOMY_WRITE_PERMISSION } from "../../constants/permissions";
import { useQuery } from "../../hooks/useApi";
import { usePermissions } from "../../hooks/usePermissions";
import {
  changedFields,
  IndustrySectorFormModal,
  type IndustrySectorFormValues,
} from "./IndustrySectorFormModal";
import styles from "./IndustrySectors.module.css";

type FormState = { open: false } | { open: true; sector: IndustrySector | null };

function byName(a: IndustrySector, b: IndustrySector): number {
  return a.name.localeCompare(b.name);
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export default function IndustrySectorsPage() {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission(TAXONOMY_WRITE_PERMISSION);
  const canDelete = hasPermission(TAXONOMY_DELETE_PERMISSION);

  const { data, loading, error, refetch, isRefetching } = useQuery(
    () => industrySectors.getAll(),
    [],
  );
  const sectors = useMemo(() => [...(data ?? [])].sort(byName), [data]);

  const [form, setForm] = useState<FormState>({ open: false });
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const busyRef = useRef(false);

  async function runAction(action: () => Promise<unknown>, successMessage: string) {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      await action();
      setToast({ message: successMessage, type: "success" });
    } catch (actionError) {
      setToast({ message: errorMessage(actionError), type: "error" });
    } finally {
      await refetch();
      busyRef.current = false;
    }
  }

  function setActive(sector: IndustrySector, active: boolean) {
    void runAction(
      () =>
        active ? industrySectors.reactivate(sector.id) : industrySectors.deactivate(sector.id),
      `${sector.name} ${active ? "reactivated" : "deactivated"}`,
    );
  }

  async function handleSubmit(values: IndustrySectorFormValues) {
    if (!form.open) return;
    const { sector } = form;
    if (sector) {
      const changes = changedFields(sector, values);
      if (Object.keys(changes).length > 0) await industrySectors.update(sector.id, changes);
    } else {
      await industrySectors.create({ name: values.name });
    }
    setForm({ open: false });
    setToast({
      message: sector ? `${values.name} saved` : `${values.name} created`,
      type: "success",
    });
    await refetch();
  }

  const columns: Column<IndustrySector>[] = [
    { header: "Name", accessor: "name" },
    { header: "Slug", accessor: "slug" },
    {
      header: "Status",
      accessor: "isActive",
      render: (_value: unknown, row: IndustrySector) => (
        <span className={`${styles.status} ${row.isActive ? styles.active : styles.inactive}`}>
          {row.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    ...(canWrite || canDelete
      ? [
          {
            header: "Actions",
            accessor: "id" as const,
            render: (_value: unknown, row: IndustrySector) => (
              <div className={styles.actions}>
                {canWrite && (
                  <Button
                    variant="outlined"
                    size="small"
                    aria-label={`Edit ${row.name}`}
                    onClick={() => setForm({ open: true, sector: row })}
                  >
                    Edit
                  </Button>
                )}
                {canDelete &&
                  (row.isActive ? (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      aria-label={`Deactivate ${row.name}`}
                      onClick={() => setActive(row, false)}
                    >
                      Deactivate
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      aria-label={`Reactivate ${row.name}`}
                      onClick={() => setActive(row, true)}
                    >
                      Reactivate
                    </Button>
                  ))}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Industry Sectors"
        subtitle="Sectors that GC Powerlist entries are classified against"
      >
        {canWrite && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={
              <span aria-hidden="true">
                <Icon icon="plus" size="small" />
              </span>
            }
            onClick={() => setForm({ open: true, sector: null })}
          >
            New sector
          </Button>
        )}
      </PageHeader>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}

      {loading && data === null ? (
        <LoadingState message="Loading industry sectors..." />
      ) : !error && sectors.length === 0 ? (
        <EmptyState
          variant="empty"
          title="No industry sectors"
          message="No industry sectors have been added yet."
        />
      ) : (
        !error && (
          <div className={styles.tableContainer}>
            <DataTable
              data={sectors}
              columns={columns}
              rowKey="id"
              rowClassName={row => (row.isActive ? "" : styles.inactiveRow)}
              emptyMessage="No industry sectors found."
            />
          </div>
        )
      )}

      <IndustrySectorFormModal
        isOpen={form.open}
        sector={form.open ? form.sector : null}
        onClose={() => setForm({ open: false })}
        onSubmit={handleSubmit}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === "error" ? 5000 : 3000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
